import React, { useState, useMemo, useRef, useEffect, useCallback } from 'react'
import YumButton from '../../../../components/YumButton/YumButton'
import { isRtl } from '../../../../i18nInit'
import { Box, FormGroup, List, ListItem, ListItemIcon, ListItemText, SwipeableDrawer } from '@material-ui/core'
import YumButtonGroup from '../../../../components/YumButtonGroup/YumButtonGroup'
import { t } from 'ttag'
import { Capacitor } from '@capacitor/core'
import Typography from '@material-ui/core/Typography'
import { activityTypes, activityTypesWithCustomAndBonus } from '../../activityTypes'
import { ignoreCategoriesForPoints } from '../ChallengePoints/ChallengePoints'
import { YumActivityIcon } from '../../../../components/YumActivityIcon/YumActivityIcon'
import { CustomTypeCategory } from '../../../../services/CustomTypes'
import { bgGray, lighterBlue, red } from '../../../../styles/material-ui-theme-v2'
import YumSearchField from '../../../../components/YumSearchField/YumSearchField'
import YumBlueTooltip from '../../../../components/YumBlueTooltip/YumBlueTooltip'
import { dailyMaxLabel } from '../../../ChallengeInfo/ChallengeInfo'
import InputAdornment from '@material-ui/core/InputAdornment'
import YumField from '../../../../components/YumInput/YumInput'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'

export interface IChallengeActivitySelectProps {
  isIconSelect?: boolean
  selectedActivity?: any // Current selected activity from parent
  onActivitySelect?: (activity: any) => void // Callback when activity is selected
  onSelect?: (activity: any) => void // Callback when Select button is clicked
  // New props for points logic integration
  // When isIconSelect is false and these props are provided, activities already used in other fields will be disabled
  fields?: any[] // Array of existing fields from points logic
  currentFieldIndex?: number // Index of the current field being edited
}

// Single source of truth for activity groups configuration
// Using array to maintain exact order when rendering
export const ACTIVITY_GROUPS_CONFIG = [
  {
    title: t`Most popular`,
    activities: [
      CustomTypeCategory.STEPS,
      CustomTypeCategory.DISTANCE,
      CustomTypeCategory.SLEEP,
      CustomTypeCategory.EXERCISE,
    ],
  },
  {
    title: t`Physical Activity`,
    activities: [
      CustomTypeCategory.RUNNING,
      CustomTypeCategory.CYCLING,
      CustomTypeCategory.SWIMMING,
      CustomTypeCategory.INTENSIVE_MINUTES,
    ],
  },
  {
    title: t`Environmental Exposure`,
    activities: [
      CustomTypeCategory.WALKING,
      CustomTypeCategory.OUTDOOR_TIME,
      CustomTypeCategory.NATURAL_LIGHT_EXPOSURE,
      CustomTypeCategory.CAR_FREE_DAYS,
    ],
  },
  {
    title: t`Strength & Conditioning`,
    activities: [
      CustomTypeCategory.STRENGTH_TRAINING,
      CustomTypeCategory.PUSH_UPS,
      CustomTypeCategory.PULL_UPS,
      CustomTypeCategory.SQUATTING,
      CustomTypeCategory.PLANKING,
    ],
  },
  {
    title: t`Mental Wellness`,
    activities: [
      CustomTypeCategory.MINDFULNESS,
      CustomTypeCategory.DEEP_BREATHING,
      CustomTypeCategory.YOGA,
      CustomTypeCategory.GRATITUDE_JOURNALING,
    ],
  },
  {
    title: t`Nutrition & Lifestyle`,
    activities: [
      CustomTypeCategory.WATER_DRINKING,
      CustomTypeCategory.HEALTHY_COOKING,
      CustomTypeCategory.INTERMITTENT_FASTING,
      CustomTypeCategory.DAILY_FLOSSING,
    ],
  },
  {
    title: t`Self-Development & Productivity`,
    activities: [
      CustomTypeCategory.DAILY_JOURNALING,
      CustomTypeCategory.FINANCE_TRACKING,
      CustomTypeCategory.HOBBY_DEVELOPMENT,
      CustomTypeCategory.CREATIVE_ARTS,
    ],
  },
  {
    title: t`Cognitive & Social Growth`,
    activities: [
      CustomTypeCategory.LANGUAGE_LEARNING,
      CustomTypeCategory.BOOK_READING,
      CustomTypeCategory.SOCIALIZING_WITH_FRIENDS,
      CustomTypeCategory.ENCOURAGING,
    ],
  },
  {
    title: t`Digital & Substance Moderation`,
    activities: [
      CustomTypeCategory.ALCOHOL_ABSTINENCE,
      CustomTypeCategory.SMOKING_CESSATION,
      CustomTypeCategory.PHONE_FREE_TIME,
      CustomTypeCategory.SCREEN_FREE_MEALS,
    ],
  },
  {
    title: t`Recovery`,
    activities: [CustomTypeCategory.COLD_PLUNGE, CustomTypeCategory.CONTRAST_SHOWER],
  },
  {
    title: t`Custom`,
    activities: (isIconSelect: boolean) =>
      isIconSelect
        ? [CustomTypeCategory.CUSTOM_SIMPLE]
        : [CustomTypeCategory.CUSTOM_SIMPLE, CustomTypeCategory.CUSTOM_WITH_AMOUNT],
  },
  {
    title: t`Bonus points`,
    activities: (isIconSelect: boolean) =>
      isIconSelect
        ? [CustomTypeCategory.BONUS_SIMPLE, CustomTypeCategory.ALL]
        : [CustomTypeCategory.BONUS_SIMPLE, CustomTypeCategory.BONUS_WITH_AMOUNT],
  },
]

// Reusable component for activity groups
interface ActivityGroupProps {
  title: string
  activityValues: any[] // Array of activity values to filter from activityTypes
  selectedActivity: any
  onActivitySelect: (value: any) => void
  filteredActivities: Array<{
    label: string
    value: any
    description?: string
  }>
  showLabels?: boolean // Optional prop to control label visibility - defaults to true
  // New props for points logic integration
  fields?: any[] // Array of existing fields from points logic
  currentFieldIndex?: number // Index of the current field being edited
  screenWidth?: number // Current screen width for responsive grid layout
}

const ActivityGroup: React.FC<ActivityGroupProps> = ({
  title,
  activityValues,
  selectedActivity,
  onActivitySelect,
  filteredActivities,
  showLabels = true,
  fields,
  currentFieldIndex,
  screenWidth,
}) => {
  // Filter activities based on the provided activity values and maintain the order from configuration
  const groupFilteredActivities = activityValues
    .map((activityValue) => filteredActivities.find((activity) => activity.value === activityValue))
    .filter((activity): activity is { label: string; value: any; description?: string } => activity !== undefined)

  // Don't render the group if no activities match the search
  if (groupFilteredActivities.length === 0) {
    return null
  }

  // Helper function to check if an activity is disabled
  const isActivityDisabled = (activityValue: any): boolean => {
    // If we're in icon select mode or don't have fields data, nothing is disabled
    if (!fields || currentFieldIndex === undefined) {
      return false
    }

    // Never disable custom and bonus activities - they can be used multiple times
    const customAndBonusActivities = [
      CustomTypeCategory.CUSTOM_SIMPLE,
      CustomTypeCategory.CUSTOM_WITH_AMOUNT,
      CustomTypeCategory.BONUS_SIMPLE,
      CustomTypeCategory.BONUS_WITH_AMOUNT,
    ]

    if (customAndBonusActivities.includes(activityValue)) {
      return false
    }

    // Check if this activity is already used in another field
    // This prevents duplicate activities in the points logic
    return fields.some((field, index) => field.category === activityValue && index !== currentFieldIndex)
  }

  const renderActivityItem = (
    {
      label,
      value,
      description,
    }: {
      label: string
      value: any
      description?: string
    },
    index: number,
  ) => {
    const labelId = `type-${index}`
    const isDisabled = isActivityDisabled(value)

    return (
      <div
        key={index}
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          width: '100%',
          //minHeight: '80px',
        }}
      >
        <ListItem
          role={undefined}
          button
          disableRipple
          selected={selectedActivity === value}
          onClick={() => !isDisabled && onActivitySelect(value)}
          style={{
            justifyContent: 'center',
            width: '48px',
            height: '48px',
            borderRadius: '10px',
            background: lighterBlue,
            margin: '0px',
            opacity: isDisabled ? 0.5 : 1,
            cursor: isDisabled ? 'not-allowed' : 'pointer',
          }}
        >
          <ListItemIcon>
            {selectedActivity === value ? (
              <YumActivityIcon category={value} />
            ) : (
              <YumActivityIcon category={value} color={'darkBlue'} isInactive={true} />
            )}
          </ListItemIcon>
          {/*<ListItemText
            id={labelId}
            primary={label}
            secondary={description}
            style={description ? { marginTop: '0px', marginBottom: '0px' } : {}}
            secondaryTypographyProps={{ style: { fontSize: '14px' } }}
          />*/}
        </ListItem>
        {showLabels && (
          <div
            onClick={() => !isDisabled && onActivitySelect(value)}
            style={{
              fontSize: '11px',
              fontStyle: 'normal',
              fontWeight: 400,
              textAlign: 'center',
              marginTop: '10px',
              width: '100%',
              lineHeight: '1.2',
              overflow: 'hidden',
              wordBreak: 'break-word',
              textOverflow: 'ellipsis',
              display: '-webkit-box',
              WebkitLineClamp: 3,
              WebkitBoxOrient: 'vertical',
              opacity: isDisabled ? 0.5 : 1,
              cursor: isDisabled ? 'not-allowed' : 'pointer',

              // overflowWrap: 'break-word',
              // wordBreak: 'normal',
              // hyphens: 'auto',
              // maxWidth: '64px',

              // // @ts-ignore
              //textWrap: 'balance',
            }}
          >
            {label}
          </div>
        )}
      </div>
    )
  }

  return (
    <>
      {showLabels ? (
        <div
        // style={{
        //   background: 'white',
        //   borderRadius: '8px',
        //   padding: '16px',
        // }}
        >
          <Typography variant="h3" style={{ marginBottom: '16px' }}>
            {title}
          </Typography>
          <div
            style={{
              display: 'grid',
              // Use 2 columns if group has exactly 2 items, or if screen width is 320px or less
              // Otherwise use 4 columns for better layout on larger screens
              gridTemplateColumns:
                /*groupFilteredActivities.length === 2 ||*/ screenWidth && screenWidth <= 320
                  ? 'repeat(2, 1fr)'
                  : 'repeat(4, 1fr)',
              gap: '10px',
              alignItems: 'start',
            }}
          >
            {groupFilteredActivities.map(renderActivityItem)}
          </div>
        </div>
      ) : (
        groupFilteredActivities.map(renderActivityItem)
      )}
    </>
  )
}

export default function ChallengeActivitySelect(props: IChallengeActivitySelectProps) {
  const {
    isIconSelect,
    selectedActivity: propSelectedActivity,
    onActivitySelect: propOnActivitySelect,
    onSelect,
    fields,
    currentFieldIndex,
  } = props

  const [isDrawerOpen, setIsDrawerOpen] = useState(false)
  const [activityType, setActivityType] = useState(propSelectedActivity || CustomTypeCategory.STEPS)
  const [tempActivityType, setTempActivityType] = useState(propSelectedActivity || CustomTypeCategory.STEPS) // Temporary selection for display
  const [isMobile, setIsMobile] = useState(window.innerWidth < 960)
  const [screenWidth, setScreenWidth] = useState(window.innerWidth)
  const [searchTerm, setSearchTerm] = useState('')

  // Handle window resize for responsive design
  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth
      setIsMobile(width < 960)
      setScreenWidth(width)
    }

    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  // Update internal state when prop changes
  useEffect(() => {
    if (propSelectedActivity !== undefined) {
      setActivityType(propSelectedActivity)
      setTempActivityType(propSelectedActivity) // Also update temporary selection
    }
  }, [propSelectedActivity])

  // Handle activity selection - only update temporary state for display
  const handleActivitySelect = (activity: any) => {
    setTempActivityType(activity) // Only update temporary selection, don't call parent callback yet
  }

  // Handle Select button click - apply the temporary selection
  const handleSelect = () => {
    setActivityType(tempActivityType) // Apply the temporary selection to actual state
    if (propOnActivitySelect) {
      propOnActivitySelect(tempActivityType) // Notify parent of the change
    }
    if (onSelect) {
      onSelect(tempActivityType)
    }
    setIsDrawerOpen(false) // Close the drawer after selection
  }

  // Handle Reset button click - reset to original selection
  const handleReset = () => {
    const defaultActivity = propSelectedActivity || CustomTypeCategory.STEPS
    setTempActivityType(defaultActivity) // Reset temporary selection to original
    setIsDrawerOpen(false) // Close the drawer after selection
  }

  // Filter activity types based on search term
  // This enhanced search now looks for matches in both:
  // 1. Individual activity labels (e.g., "Running", "Cycling")
  // 2. Group titles (e.g., "Physical Activity", "Mental Wellness")
  // When a group title matches, all activities from that group are included in results
  const filteredActivityTypes = useMemo(() => {
    if (!searchTerm.trim()) return activityTypesWithCustomAndBonus

    // First, get activities that match the search term directly
    const directMatches = activityTypesWithCustomAndBonus.filter((activity) =>
      activity.label.toLowerCase().includes(searchTerm.toLowerCase()),
    )

    // Then, find all group titles that match the search term
    const matchingGroups = ACTIVITY_GROUPS_CONFIG.filter((group) =>
      group.title.toLowerCase().includes(searchTerm.toLowerCase()),
    )

    // If any group titles match, include all activities from those groups
    if (matchingGroups.length > 0) {
      const allGroupActivities = new Set(directMatches.map((activity) => activity.value))

      // Add activities from matching groups
      matchingGroups.forEach((group) => {
        const groupActivities =
          typeof group.activities === 'function' ? group.activities(isIconSelect ?? false) : group.activities

        groupActivities.forEach((activityValue: any) => allGroupActivities.add(activityValue))
      })

      // Return all activities that are either direct matches or in matching groups
      return activityTypesWithCustomAndBonus.filter((activity) => allGroupActivities.has(activity.value))
    }

    // If no group titles match, return only direct matches
    return directMatches
  }, [searchTerm, isIconSelect])

  // Get the human-readable label for the current activity type
  const activityTypeLabel = useMemo(() => {
    const activity = activityTypesWithCustomAndBonus.find((a) => a.value === activityType) // Use actual selection for display
    return activity ? activity.label : activityType
  }, [activityType])

  const toggleDrawer = useCallback(
    (open: boolean) => (event: React.KeyboardEvent | React.MouseEvent) => {
      if (
        event &&
        event.type === 'keydown' &&
        ((event as React.KeyboardEvent).key === 'Tab' || (event as React.KeyboardEvent).key === 'Shift')
      ) {
        return
      }

      if (!open) {
        // When closing the drawer, reset temporary selection to current actual selection
        // This ensures changes are discarded if user doesn't click "Select"
        setTempActivityType(activityType)
      } else {
        // When opening the drawer, initialize temporary selection with current actual selection
        setTempActivityType(activityType)
      }

      setIsDrawerOpen(open)
    },
    [activityType],
  )

  const iOS = Capacitor.getPlatform() === 'ios'
  const showLabels = !isIconSelect

  const supportsCustomIcon = [
    CustomTypeCategory.CUSTOM_SIMPLE,
    CustomTypeCategory.CUSTOM_WITH_AMOUNT,
    CustomTypeCategory.BONUS_SIMPLE,
    CustomTypeCategory.BONUS_WITH_AMOUNT,
  ].includes(activityType)
  const showIcon = !supportsCustomIcon

  // Generate activity groups from the single configuration
  const groups = ACTIVITY_GROUPS_CONFIG.map(({ title, activities }) => {
    const values = typeof activities === 'function' ? activities(isIconSelect ?? false) : activities
    return (
      <ActivityGroup
        key={title}
        title={title}
        activityValues={values}
        selectedActivity={tempActivityType} // Use temporary selection for display
        onActivitySelect={handleActivitySelect}
        filteredActivities={filteredActivityTypes}
        showLabels={showLabels}
        fields={fields}
        currentFieldIndex={currentFieldIndex}
        screenWidth={screenWidth}
      />
    )
  })

  return (
    <>
      {isIconSelect ? (
        <FormGroup>
          <YumBlueTooltip
            title={t`Choose custom icon`}
            placement="top"
            enterTouchDelay={50}
            leaveTouchDelay={3000}
            arrow
            PopperProps={{
              container: document.getElementById('content'),
              disablePortal: true,
            }}
          >
            <ListItem style={{ height: '48px' }} role={undefined} dense button onClick={toggleDrawer(true)}>
              <ListItemIcon>
                <YumActivityIcon
                  category={propSelectedActivity}
                  color="darkBlue"
                  isInactive={true}
                  //bg="white"
                />
              </ListItemIcon>
            </ListItem>
          </YumBlueTooltip>
        </FormGroup>
      ) : (
        <YumField
          label={t`Activity type`}
          type="text"
          value={activityTypeLabel || undefined}
          onClick={toggleDrawer(true)}
          InputProps={{
            ...(showIcon
              ? {
                  startAdornment: (
                    <InputAdornment position={'start'} style={{ marginTop: 0 }}>
                      <YumActivityIcon category={activityType} color="darkBlue" isInactive={true} />
                    </InputAdornment>
                  ),
                }
              : {}),
            endAdornment: (
              <InputAdornment position={'end'} style={{ marginTop: 0 }}>
                <ExpandMoreIcon style={{ color: '#666', fontSize: '20px' }} />
              </InputAdornment>
            ),
            style: {
              cursor: 'pointer',
            },
          }}
          normalIconClass={true}
          inputProps={{
            readOnly: true,
            id: 'activity-type',
            style: {
              cursor: 'pointer',
            },
          }}
        />
      )}

      <SwipeableDrawer
        anchor="bottom"
        open={isDrawerOpen}
        onClose={toggleDrawer(false)}
        onOpen={toggleDrawer(true)}
        disableBackdropTransition={!iOS}
        disableDiscovery={iOS}
        PaperProps={{
          style: {
            display: 'flex',
            flexDirection: 'column',
            overflowY: 'hidden',
            //background: bgGray,
            maxWidth: '440px', //isMobile ? '425px' : '816px',
          },
        }}
      >
        <div style={{ flexGrow: 1, padding: isMobile ? '0' : '8px 10px 0 10px', overflowY: 'auto' }}>
          <Typography variant="h6" style={{ marginBottom: '20px' }}>
            {isIconSelect ? t`Choose icon` : t`Choose activity`}
          </Typography>

          <div
            style={{
              // display: 'grid',
              // gridTemplateColumns: !showLabels || isMobile ? '1fr' : 'repeat(2, 1fr)',
              // gap: '16px',
              display: 'flex',
              flexDirection: 'column',
              gap: '20px',
              paddingBottom: '20px',
            }}
          >
            {showLabels ? (
              groups
            ) : (
              <div
                style={{
                  // background: 'white',
                  // borderRadius: '8px',
                  // padding: '16px',

                  display: 'grid',
                  gap: '10px',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(48px, 1fr))',
                }}
              >
                {groups}
              </div>
            )}
          </div>
        </div>

        <div
          style={{
            flexShrink: 0,
            padding: isMobile ? '10px 0' : '20px 10px',
          }}
        >
          <div style={{ marginBottom: isMobile ? '10px' : '20px' }}>
            <YumSearchField value={searchTerm} onChange={setSearchTerm} /*whiteBackground={true}*/ autoFocus={false} />
          </div>
          <YumButtonGroup>
            <YumButton variant="outlined" onClick={handleReset}>{t`Reset`}</YumButton>
            <YumButton onClick={handleSelect}>{t`Select`}</YumButton>
          </YumButtonGroup>
        </div>
      </SwipeableDrawer>
    </>
  )
}
